import type * as React from 'react';

import { ChatList } from '@/components/ChatBot/components/ChatList';
import {
  BotError,
  BotLoader,
  BotMessage,
  UserMessage,
} from '@/features/ChatBotV2/components/Message';
import { BotStepper } from '@/features/ChatBotV2/components/Message/BotStepper';
import { Placeholder } from '@/features/ChatBotV2/components/Placeholder';
import type { AskNeedlReportType } from '@/features/ChatBotV2/context';
import { withAskNeedlStateSlice } from '@/features/ChatBotV2/hooks/withStateSlice';
import type { SessionState } from '@/features/ChatBotV2/types';

const ChatContainerImpl = ({
  listContainerRef,
  isError,
  isLoading,
  handleAskQuestion,
  sessionState,
  pro,
  report,
  lastActiveSessionId,
  attachmentsFeedId,
}: ChatContainerImplType) => {
  return (
    <ChatList ref={listContainerRef}>
      {sessionState.messages.length === 0 ? (
        <Placeholder
          handleAskQuestion={handleAskQuestion}
          pro={pro}
          sessionState={sessionState}
          report={report}
          lastActiveSessionId={lastActiveSessionId}
        />
      ) : (
        <>
          {sessionState.messages.map((message, i) =>
            message.type === 'user' ? (
              <UserMessage
                isReportMessage={report.enabled}
                key={message.id}
                prompt={message.prompt}
                attachments={message.attachments}
                attachmentsFeedId={attachmentsFeedId}
              />
            ) : (
              <>
                {message.intermediateSteps?.length > 0 ? (
                  <BotStepper
                    steps={message.intermediateSteps}
                    loading={
                      i === sessionState.messages.length - 1 ? isLoading : false
                    }
                  />
                ) : null}
                {message.error ? (
                  <BotError isReportAnswer={report.enabled} key={message.id} />
                ) : message.data || message.streamingAnswer ? (
                  <BotMessage
                    key={message.id}
                    streamingAnswer={message.streamingAnswer ?? ''}
                    answerMarkdown={message.answerMarkdown}
                    data={message.data}
                    formattedData={message.formattedData}
                    prompt={message.prompt}
                    generatingStopped={message.generatingStopped}
                    isReportAnswer={report.enabled}
                    report_type={report.info?.id}
                    handleAskQuestion={handleAskQuestion}
                  />
                ) : null}
              </>
            )
          )}
          {isLoading ? <BotLoader isReportAnswer={report.enabled} /> : null}
          {isError ? <BotError isReportAnswer={report.enabled} /> : null}
        </>
      )}
    </ChatList>
  );
};

export const ChatContainer = withAskNeedlStateSlice<
  Record<string, unknown>,
  ChatContainerImplType
>(ChatContainerImpl, (props, state) => {
  const currentFeedOrDocumentId = state.data.currentFeedOrDocumentId;
  const searchWithin = state.data.searchWithin;

  return {
    ...props,
    listContainerRef: props.listContainerRef as React.RefObject<HTMLDivElement>,
    isLoading: props.isLoading as boolean,
    isError: props.isError as boolean,
    pro: state.data.sessions[searchWithin][currentFeedOrDocumentId].pro,
    handleAskQuestion: props.handleAskQuestion as () => void,
    sessionState: state.data.sessions[searchWithin][currentFeedOrDocumentId],
    report: state.data.report,
    lastActiveSessionId: state.data.lastActiveSessionId,
    attachmentsFeedId:
      state.data.sessions[searchWithin][currentFeedOrDocumentId]
        .attachmentsFeedId,
  };
});

type ChatContainerImplType = {
  listContainerRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  isError: boolean;
  handleAskQuestion: () => void;
  sessionState: SessionState;
  pro: boolean;
  report: AskNeedlReportType;
  lastActiveSessionId: string | null;
  attachmentsFeedId?: string;
};
